/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Flex,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  TableScrollbar,
  Text,
  Truncate,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { AngleRightIcon } from '@tonic-one/react-icons';
import React, { useCallback, useMemo } from 'react';
import {
  DlpTemplateItem,
  MipLabelItem,
  TableColumn,
} from 'src/pages/Policy/utils/types';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';
import TargetList from 'src/components/TargetList';
import { useWhyDidYouUpdate } from 'src/utils/hooks';
import { useDlpConditionStatementStyle } from '@Policy/DataLossPrevention/PolicyTemplate/Filters/Dlp/style';

export interface TableRow<T> {
  id: string;
  cells: {
    id: string;
    column: TableColumn;
    row: T;
  }[];
  row: T;
}

export const FlexContainer = React.memo(
  ({ style, ...props }: { [key: string]: any }) => {
    // console.log(style);
    useWhyDidYouUpdate('DlpTemplateTable FlexContainer', { style, ...props });
    const { flexContianerStyle } = useDlpConditionStatementStyle();
    const newStyle = { ...flexContianerStyle, ...style };
    return <Flex style={newStyle} {...props} />;
  }
);

export const SelectedTable = React.memo(
  (props: {
    tableStyle: any;
    columns: TableColumn[];
    rows: TableRow<any>[];
  }) => {
    const { tableStyle, columns, rows } = props;
    const { hoverBackgroundColor, tableHeaderStyleProps } =
      useDlpConditionStatementStyle();
    const tableHeaderRef = React.useRef<HTMLDivElement>(null);
    const onScrollTableBody = React.useCallback((e: any) => {
      const tableHeader = tableHeaderRef?.current;
      if (!tableHeader) {
        return;
      }
      // Sync the scroll position of the table header and table body for horizontal scrolling
      const scrollLeft = e.target.scrollLeft;
      if (tableHeader.scrollLeft !== scrollLeft) {
        tableHeader.scrollLeft = scrollLeft;
      }
    }, []);
    return (
      <Table layout="flexbox" variant="default" size="sm" {...tableStyle}>
        <TableHeader ref={tableHeaderRef} {...tableHeaderStyleProps}>
          <TableHeaderRow>
            {columns.map((column) => {
              const styleProps = {
                flex: 'none',
                width: column.size,
                ...column.style,
              };
              return (
                <TableHeaderCell key={column.id} {...styleProps}>
                  <Truncate>{column.header}</Truncate>
                </TableHeaderCell>
              );
            })}
          </TableHeaderRow>
        </TableHeader>
        <TableScrollbar
          height="100%"
          overflow="visible" // Make the scrollbar visible
          onScroll={onScrollTableBody} // To sync the scroll position of the table header and table body for horizontal scrolling
        >
          <TableBody>
            {rows.map((row) => {
              const styleProps = {
                _hover: {
                  backgroundColor: hoverBackgroundColor,
                },
              };
              return (
                <TableRow key={row.id} {...styleProps}>
                  {row.cells.map((cell) => {
                    const styleProps = {
                      flex: 'none',
                      width: cell.column.size,
                      ...cell.column.style,
                    };
                    return (
                      <TableCell key={cell.id} {...styleProps}>
                        {typeof cell.column.cell === 'function' ? (
                          cell.column.cell(row.row)
                        ) : (
                          <Truncate>
                            {
                              cell.row[
                                cell.column.accessorKey as keyof typeof cell.row
                              ]
                            }
                          </Truncate>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </TableScrollbar>
      </Table>
    );
  },
  isEqual
);
export interface ComplianceRuleTransferProps {
  mipShow: boolean;
  mipLabels: MipLabelItem[];
  dlpTemplates: DlpTemplateItem[];
  dlpRows: TableRow<any>[];
  selectedDlpTemplateColumns: TableColumn[];
  addDlpTemplatesAndMipLabels: Function;
  addDlpTemplates: Function;
  handleAddModalOpen: Function;
}
export const ComplianceRuleTransfer = React.memo(
  (props: ComplianceRuleTransferProps) => {
    const {
      mipShow,
      mipLabels,
      dlpTemplates,
      dlpRows,
      selectedDlpTemplateColumns,
      addDlpTemplatesAndMipLabels,
      addDlpTemplates,
      handleAddModalOpen,
    } = props;
    const { t } = useTranslation();
    const [colorMode] = useColorMode();
    const [colorStyle] = useColorStyle({ colorMode });
    // local state
    const [isBrightRight, setIsBrightRight] = React.useState(false);
    const [checkedDlpTemplates, setCheckedDlpTemplates] = React.useState<
      DlpTemplateItem[]
    >([]);
    const [checkedMipLabelItems, setCheckedMipLabelItems] = React.useState<
      MipLabelItem[]
    >([]);
    const checkedTemplates = React.useMemo(() => {
      return [...checkedDlpTemplates, ...checkedMipLabelItems];
    }, [checkedDlpTemplates, checkedMipLabelItems]);
    const { tableStyleProps, tableStylePropsWithMip } =
      useDlpConditionStatementStyle();
    const selectedDlpTemplatesTableStyleProps = useMemo(() => {
      return mipShow ? tableStylePropsWithMip : tableStyleProps;
    }, [mipShow]);
    const handleAddClick = useCallback(() => {
      try {
        if (mipShow) {
          addDlpTemplatesAndMipLabels(checkedTemplates);
        } else {
          addDlpTemplates(checkedTemplates as DlpTemplateItem[]);
        }
      } catch (error: any) {
        const code = error?.status || 'Unknown';
        if (code === 400 && error.data.error.details[0].code === -118) {
          window.showNotification({
            severity: 'error',
            titleMsg: t(
              'errors.policy.POLICY_API;UPSERT_TEMPLATE_FAILURE;-118'
            ),
          });
        } else {
          window.showNotification({
            severity: 'error',
            titleMsg: t('common.apiException.internalError'),
          });
        }
      }
    }, [mipShow, checkedTemplates]);
    const handleSelectMipLabelItem = useCallback((items: any[]) => {
      setCheckedMipLabelItems(items);
    }, []);
    const handleSelectDlpTemplate = useCallback((items: any[]) => {
      setCheckedDlpTemplates(items);
    }, []);
    React.useEffect(() => {
      if (checkedTemplates.length > 0) {
        setIsBrightRight(true);
      } else {
        setIsBrightRight(false);
      }
    }, [checkedTemplates]);
    return (
      <Flex alignItems="center" mt="12px">
        <Flex direction="column">
          {mipShow ? (
            <TargetList
              title={t('policy.dlp.complianceRules.availableSensitivityLabel')}
              disabled={false}
              list={mipLabels}
              limit={500}
              width="368px"
              size={9}
              multipleSelect={true}
              onSelect={handleSelectMipLabelItem}
            />
          ) : null}
          <TargetList
            title={t('policy.dlp.complianceRules.availableTemplate')}
            disabled={false}
            list={dlpTemplates}
            onEdit={handleAddModalOpen}
            limit={500}
            width="368px"
            size={9}
            multipleSelect={true}
            onSelect={handleSelectDlpTemplate}
          />
        </Flex>
        <Flex
          direction="column"
          alignItems="center"
          justifyContent="center"
          ml="12px"
          mr="12px"
          width="24px"
        >
          <Button
            variant="secondary"
            onClick={handleAddClick}
            mb="8px"
            width="24px"
            size="sm"
          >
            <Icon
              as={AngleRightIcon}
              color={isBrightRight ? 'blue:50' : colorStyle.color.primary}
            />
          </Button>
        </Flex>
        <FlexContainer direction="column">
          <Flex mb="12px">
            <Text>{t('policy.dlp.selectedDlpTemplates')}</Text>
          </Flex>
          <SelectedTable
            columns={selectedDlpTemplateColumns}
            tableStyle={selectedDlpTemplatesTableStyleProps}
            rows={dlpRows}
          />
        </FlexContainer>
      </Flex>
    );
  },
  isEqual
);
